// src/components/molecules/configuration/privateVehicles/createVehicles/schema.ts

import * as Yup from 'yup';
import { isValidLicensePlate } from '../../../../../utils/isValidLicensePlate';
import { removeSpecialChars } from '../../../../../services/mask';

const fileSchema = Yup.mixed()
  .nullable()
  .test('fileType', 'Tipo de arquivo inválido', value => {
    if (!value) return true;
    if (value instanceof File) {
      return [
        'image/jpeg',
        'image/png',
        'image/gif',
        'application/pdf',
      ].includes(value.type);
    }
    return typeof value === 'string';
  })
  .test('fileSize', 'Arquivo muito grande (máx. 5MB)', value => {
    if (!value) return true;
    if (value instanceof File) {
      return value.size <= 5 * 1024 * 1024;
    }
    return true;
  });

const singleVehicleObjectSchema = Yup.object()
  .shape({
    id: Yup.string()
      .required('ID do veículo é obrigatório.')
      .uuid('ID deve ser um UUID válido.'),
    model: Yup.string()
      .required('O modelo do veículo é obrigatório')
      .max(100, 'O modelo não pode ter mais de 100 caracteres'),
    year: Yup.number()
      .typeError('O ano deve ser um número')
      .required('O ano do veículo é obrigatório')
      .integer('O ano deve ser um número inteiro')
      .min(1900, 'O ano mínimo permitido é 1900')
      .max(
        new Date().getFullYear() + 1,
        'O ano não pode ser maior que o ano atual + 1',
      ),
    licensePlate: Yup.string()
      .required('A placa do veículo é obrigatória')
      .matches(
        /^[A-Z]{3}\d{4}$|^[A-Z]{3}\d[A-Z]\d{2}$/,
        'Formato de placa inválido (AAA1234 ou AAA1A23)',
      )
      .test('isValidPlate', 'A placa do veículo não é válida', value => {
        const cleanedValue = removeSpecialChars(value || '');
        return isValidLicensePlate(cleanedValue);
      }),
    payloadCapacity: Yup.number()
      .typeError('A capacidade de carga deve ser um número')
      .required('A capacidade de carga é obrigatória')
      .positive('A capacidade de carga deve ser um número positivo')
      .max(99999, 'A capacidade de carga máxima é de 99999 kg'),
    vehicleType: Yup.string()
      .required('O tipo de veículo é obrigatório'),
    dimensions: Yup.object()
      .shape({
        weight: Yup.number()
          .typeError('O peso deve ser um número')
          .required('O peso é obrigatório')
          .positive('O peso deve ser um número positivo')
          .max(99999, 'O peso máximo é de 99999 kg'),
        length: Yup.number()
          .typeError('O comprimento do baú deve ser um número')
          .required('O comprimento do baú é obrigatório')
          .positive('O comprimento do baú deve ser um número positivo')
          .max(20, 'O comprimento máximo do baú é de 20 metros'),
        width: Yup.number()
          .typeError('A largura do baú deve ser um número')
          .required('A largura do baú é obrigatória')
          .positive('O largura do baú deve ser um número positivo')
          .max(5, 'A largura máxima do baú é de 5 metros'),
      })
      .required('As dimensões do veículo são obrigatórias'),

    crlv: Yup.object()
      .shape({
        documentNumber: Yup.string()
          .required('O número do documento CRLV é obrigatório')
          .matches(
            /^\d{11}$/,
            'O número do documento CRLV deve ter 11 dígitos numéricos',
          ),
        expirationDate: Yup.string()
          .required('A data de vencimento do CRLV é obrigatória'),
        // .test(
        //   'is-valid-date-format',
        //   'Formato da data de vencimento inválido (DD/MM/YYYY)',
        //   function (value) {
        //     if (!value) return false;
        //     const parts = value.split('/');
        //     if (parts.length !== 2) return false;
        //     const [month, year] = parts;
        //     if (!/^\d{2}$/.test(month) || !/^\d{4}$/.test(year)) return false;
        //     const monthNum = parseInt(month, 10);
        //     const yearNum = parseInt(year, 10);

        //     if (monthNum < 1 || monthNum > 12) return false;
        //     if (yearNum < 1900 || yearNum > new Date().getFullYear() + 10)
        //       return false;

        //     return true;
        //   },
        // ),
        isDigital: Yup.boolean().required(
          'É obrigatório informar se o CRLV é físico ou digital.',
        ),
        imageUrlFront: fileSchema.required(
          'A imagem frontal do CRLV é obrigatória.',
        ),
        imageUrlBack: Yup.mixed()
          .test('required-if-phisic', 'A imagem traseira do CRLV é obrigatória quando o CRLV é fisica.', function (value) {
            const { isDigital } = this.parent;
            if (!isDigital) {
              return value != null;
            }
            return true;
          })
          .nullable()
          .test('fileType', 'Tipo de arquivo inválido', value => {
            if (!value) return true;
            if (value instanceof File) {
              return [
                'image/jpeg',
                'image/png',
                'image/gif',
                'application/pdf',
              ].includes(value.type);
            }
            return typeof value === 'string';
          })
          .test('fileSize', 'Arquivo muito grande (máx. 5MB)', value => {
            if (!value) return true;
            if (value instanceof File) {
              return value.size <= 5 * 1024 * 1024;
            }
            return true;
          })
      })
      .required('Os dados do CRLV são obrigatórios.'),

    images: Yup.object().shape({
      imageUrlFront: fileSchema,
      imageUrlBack: fileSchema,
      imageUrlLeft: fileSchema,
      imageUrlRight: fileSchema,
    }),
  })
  .required();

export interface SingleVehicleForm {
  id: string;
  model: string;
  year: string;
  licensePlate: string;
  payloadCapacity: number;
  vehicleType: string;
  dimensions: {
    weight: number;
    length: number;
    width: number;
  };
  crlv: {
    crlvId: string;
    documentNumber: string;
    expirationDate: string;
    isDigital: boolean;
    imageUrlFront: File | null;
    imageUrlBack: File | null;
  };
  images: {
    imageUrlBack: File | null;
    imageUrlFront: File | null;
    imageUrlLeft: File | null;
    imageUrlRight: File | null;
  };
}

export const vehicleSchema = Yup.object().shape({
  vehicles: Yup.array()
    .of(singleVehicleObjectSchema)
    .min(1, 'É necessário adicionar pelo menos um veículo.'),
});

export type VehicleFormSchema = Yup.InferType<typeof vehicleSchema>;

export default vehicleSchema;
